import { MOBILE_QUERY } from "../hooks/useResponsiveLayout";

export type AnalyticsEventName =
  | "session_start"
  | "page_view"
  | "map_interaction"
  | "place_view"
  | "chart_view"
  | "environmental_feed_view"
  | "filter_applied"
  | "community_report_started"
  | "community_report_submitted"
  | "guardianes_discovered"
  | "guardianes_started"
  | "guardianes_finished"
  | "guardianes_feed_navigation";

export type AnalyticsAttribution = {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
};

type AnalyticsSession = {
  sessionId: string;
  attribution: AnalyticsAttribution;
  deviceClass: "mobile" | "desktop";
  lastActivityAt: number;
};

type AnalyticsProperties = Record<string, string | number | boolean>;

const SESSION_STORAGE_KEY = "protea.analytics.session.v1";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
const API_HOST = process.env.REACT_APP_API_HOST ?? "";
const EVENT_ENDPOINT = `${API_HOST}/api/v1/analytics/events`;

const EVENT_KEYS: Record<AnalyticsEventName, readonly string[]> = {
  session_start: [],
  page_view: ["path"],
  map_interaction: ["interaction_type"],
  place_view: ["place_id", "place_category"],
  chart_view: ["place_id", "metric", "time_range"],
  environmental_feed_view: ["feed_id", "place_id"],
  filter_applied: ["filter_type", "value_category"],
  community_report_started: ["report_type"],
  community_report_submitted: ["report_type", "success"],
  guardianes_discovered: [],
  guardianes_started: ["mode", "character"],
  guardianes_finished: ["mode", "character", "score", "duration_ms", "waste_collected", "mission_completed", "cause"],
  guardianes_feed_navigation: [],
};

let lastNavigationEvent: string | null = null;
let lastNavigationAt = 0;
let memorySession: AnalyticsSession | null = null;

function randomId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
function currentDeviceClass(): "mobile" | "desktop" {
  return window.matchMedia(MOBILE_QUERY).matches ? "mobile" : "desktop";
}

function safeStorageGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Privacy settings can disable storage. Analytics remains best effort.
  }
}

function cleanTag(value: string | null, maxLength = 120): string | null {
  if (!value) return null;
  const cleaned = value.trim().toLowerCase().replace(/[^a-z0-9._-]+/g, "-").slice(0, maxLength);
  return cleaned || null;
}

function firstNonEmpty(...values: Array<string | null>): string | null {
  return values.find((value) => Boolean(value && value.trim()))?.trim() ?? null;
}

export function parseAttribution(location: Location = window.location): AnalyticsAttribution {
  const params = new URLSearchParams(location.search);
  const source = cleanTag(firstNonEmpty(params.get("utm_source"), params.get("origen")));
  const medium = cleanTag(params.get("utm_medium"));
  const campaign = cleanTag(params.get("utm_campaign"));
  const content = cleanTag(params.get("utm_content"));
  if (source) return { source, medium, campaign, content };

  if (document.referrer) {
    try {
      const referrer = new URL(document.referrer);
      if (referrer.hostname && referrer.hostname !== location.hostname) {
        return {
          source: cleanTag(referrer.hostname),
          medium: "referral",
          campaign: null,
          content: null,
        };
      }
    } catch {
      return { source: "unknown", medium: null, campaign: null, content: null };
    }
  }
  return { source: "direct", medium: null, campaign: null, content: null };
}

function readSession(): AnalyticsSession | null {
  const encoded = safeStorageGet(SESSION_STORAGE_KEY);
  if (!encoded) return null;
  try {
    const parsed = JSON.parse(encoded) as AnalyticsSession;
    if (
      !parsed.sessionId
      || !parsed.attribution
      || (parsed.deviceClass !== "mobile" && parsed.deviceClass !== "desktop")
      || typeof parsed.lastActivityAt !== "number"
    ) return null;
    return parsed;
  } catch {
    return null;
  }
}

function getSession(): { session: AnalyticsSession; isNew: boolean } {
  const now = Date.now();
  const existing = readSession() ?? memorySession;
  if (existing && now - existing.lastActivityAt < SESSION_TIMEOUT_MS) {
    existing.lastActivityAt = now;
    memorySession = existing;
    safeStorageSet(SESSION_STORAGE_KEY, JSON.stringify(existing));
    return { session: existing, isNew: false };
  }
  const session: AnalyticsSession = {
    sessionId: randomId(),
    attribution: parseAttribution(),
    deviceClass: currentDeviceClass(),
    lastActivityAt: now,
  };
  memorySession = session;
  safeStorageSet(SESSION_STORAGE_KEY, JSON.stringify(session));
  return { session, isNew: true };
}

function send(payload: object): void {
  const body = JSON.stringify(payload);
  try {
    void fetch(EVENT_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "same-origin",
    }).catch(() => undefined);
  } catch {
    // The product must remain usable when the analytics endpoint is unavailable.
  }
}

export function trackAnalyticsEvent(
  eventName: AnalyticsEventName,
  properties: AnalyticsProperties = {},
): void {
  const allowedKeys = EVENT_KEYS[eventName];
  if (Object.keys(properties).some((key) => !allowedKeys.includes(key))) return;
  if (eventName === "page_view") {
    const path = properties.path;
    if (typeof path !== "string" || !path.startsWith("/") || path.includes("?") || path.includes("#")) return;
  }
  const { session, isNew } = getSession();
  const base = {
    schema_version: 1,
    session_id: session.sessionId,
    device_class: session.deviceClass,
    occurred_at: new Date().toISOString(),
    attribution: session.attribution,
  };
  if (isNew && eventName !== "session_start") {
    send({
      ...base,
      event_name: "session_start",
      event_id: randomId(),
      properties: {},
    });
  }
  send({
    ...base,
    event_name: eventName,
    event_id: randomId(),
    properties,
  });
}

export function trackPageView(path: string): void {
  const now = Date.now();
  if (lastNavigationEvent === path && now - lastNavigationAt < 1000) return;
  lastNavigationEvent = path;
  lastNavigationAt = now;
  trackAnalyticsEvent("page_view", { path });
}

